import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { snapshotSchedule } from "../../Utils/api";
import "./SaveTemplateModal.css";


const SCOPES = [
  { value: "COMPANY_WEEK", label: "Whole company — full week (backup)" },
  { value: "WEEK", label: "This employee — full week (all days)" },
];

const SaveTemplateModal = ({
  open,
  onClose,
  onSuccess,
  activeCustomers,
  companyId,
  createdByEmpId,
  origEmpId,
  selectedEmployeeName,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("COMPANY_WEEK");
  const [isShared, setIsShared] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setTemplateName("");
    setDescription("");
    setScope("COMPANY_WEEK");
    setIsShared(true);
    setError("");
    onClose();
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      setError("Template name is required.");
      return;
    }

    if (scope === "WEEK" && !origEmpId) {
      setError("Select an employee first to save their whole week.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await snapshotSchedule({
        companyId,
        createdByEmpId,
        scope,
        employeeId: scope === "WEEK" ? origEmpId : null,
        templateName: templateName.trim(),
        description: description.trim() || null,
        isShared,
        isAutoBackup: false,
      });

      onSuccess(`Template "${templateName.trim()}" saved successfully.`);
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 409
          ? "A template with this name already exists. Please choose a different name."
          : status === 400
            ? (err?.response?.data?.message || "There is nothing scheduled to save for the selected scope.")
            : "Failed to save template. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const scopeSubtitle = () => {
    if (scope === "WEEK") return `Every day of ${selectedEmployeeName || "this employee"}'s saved week will be captured.`;
    return "Every employee's full saved week will be captured — use this as a restore point.";
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="save-template-modal">
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "8px" }}>
          Save as Template
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "16px" }}>
          {scopeSubtitle()}
        </Typography>

        <Divider sx={{ marginBottom: "16px" }} />

        <FormControl fullWidth sx={{ marginBottom: "14px" }}>
          <InputLabel>What to save</InputLabel>
          <Select
            value={scope}
            label="What to save"
            onChange={(e) => setScope(e.target.value)}
          >
            {SCOPES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 100 }}
          sx={{ marginBottom: "14px" }}
          autoFocus
        />

        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          inputProps={{ maxLength: 500 }}
          sx={{ marginBottom: "14px" }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
            />
          }
          label="Share with all managers and co-managers"
          sx={{ marginBottom: "8px" }}
        />

        {error && (
          <Typography variant="body2" color="error" sx={{ marginBottom: "10px" }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
          <Button variant="outlined" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !templateName.trim()}
            sx={{ backgroundColor: "#002244" }}
          >
            {saving ? <CircularProgress size={20} /> : "Save Template"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveTemplateModal;
